/**
 * GSGO home page (fallback when no CMS page is published for `/`).
 * Verbatim copy and section order: docs/Requirements/GSGO_Website_PRD_Build_Spec.pdf — PART 2, HP-1..HP-9
 */

import { Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState, type Ref, type RefObject } from 'react';
import { ArrowRight, ChevronDown, Lock, ShieldCheck, Heart, Play } from 'lucide-react';

import heroImage from '@assets/hero-gsgo-care.svg';
import { MonthlyWarriorSocialProof } from '@/components/donate/MonthlyWarriorSocialProof';
import { NewsletterSubscribeForm } from '@/components/NewsletterSubscribeForm';
import { pushAnalyticsEvent } from '@/lib/analytics';
import { WARRIORS, WARRIOR_TOP_BG, type WarriorAccent } from '@/data/warriors';

/* ----- PRD color tokens (Part 0 CSS variables) ----- */
const MISSION = '#1A5276';
const TEAL = '#148F77';
const GOLD = '#D4AC0D';
const URGENT = '#C0392B';
const BG = '#F2F3F4';
const INK = '#1C2833';
const MUTED = '#717D7E';

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
}

interface HomePageProps {
  branding: BrandingSettings | null;
  /** Kept for App shell compatibility; PRD home CTAs use donate + video links. */
  onOpenStrategyCall?: () => void;
}

const HERO_VIDEO = typeof import.meta.env.VITE_HERO_VIDEO_URL === 'string' ? import.meta.env.VITE_HERO_VIDEO_URL : '';
const KYLE_YT =
  typeof import.meta.env.VITE_KYLE_STORY_YOUTUBE_ID === 'string' ? import.meta.env.VITE_KYLE_STORY_YOUTUBE_ID : '';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = globalThis.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (): void => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function useCountUp(target: number, extraDeps: unknown[] = []): { ref: Ref<HTMLDivElement>; n: number } {
  const ref = useRef<HTMLDivElement | null>(null);
  const [n, setN] = useState(0);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      setN(target);
      return;
    }
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        const start = performance.now();
        const dur = 1800;
        const from = 0;
        const step = (now: number): void => {
          const t = Math.min(1, (now - start) / dur);
          const e = 1 - (1 - t) ** 3;
          setN(Math.floor(from + (target - from) * e));
          if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        obs.disconnect();
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [target, reduced, ...extraDeps]);

  return { ref: ref as Ref<HTMLDivElement>, n };
}

function CooperativeDiagram(): JSX.Element {
  return (
    <div className="w-full max-w-xl mx-auto rounded-3xl p-5 sm:p-7" style={{ backgroundColor: '#F3F4F6' }}>
      <ol className="space-y-4" aria-label="How the cooperative works">
        <li
          className="rounded-2xl px-4 py-4 sm:px-5 flex items-center gap-4"
          style={{ backgroundColor: 'rgba(26,82,118,0.08)' }}
        >
          <span
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl font-extrabold"
            style={{ backgroundColor: MISSION, color: '#fff' }}
          >
            1
          </span>
          <div>
            <p className="text-xl font-bold tracking-wide uppercase" style={{ color: MISSION }}>
              Global Donors
            </p>
            <p className="mt-0.5 text-[15px]" style={{ color: MUTED }}>
              Diaspora, corporate CSR, foundations
            </p>
          </div>
        </li>

        <div className="flex justify-center" aria-hidden="true">
          <span className="text-3xl leading-none" style={{ color: MUTED }}>↓</span>
        </div>

        <li
          className="rounded-2xl px-4 py-4 sm:px-5 flex items-center gap-4"
          style={{ backgroundColor: 'rgba(42,157,143,0.10)' }}
        >
          <span
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl font-extrabold"
            style={{ backgroundColor: TEAL, color: '#fff' }}
          >
            2
          </span>
          <div>
            <p className="text-xl font-bold tracking-wide uppercase" style={{ color: TEAL }}>
              GSGO Verification &amp; Disbursement
            </p>
            <p className="mt-0.5 text-[15px]" style={{ color: MUTED }}>
              Medical checks, tracked transfers, quarterly reports
            </p>
          </div>
        </li>

        <div className="flex justify-center" aria-hidden="true">
          <span className="text-3xl leading-none" style={{ color: MUTED }}>↓</span>
        </div>

        <li
          className="rounded-2xl px-4 py-4 sm:px-5 flex items-center gap-4"
          style={{ backgroundColor: 'rgba(212,175,55,0.14)' }}
        >
          <span
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl font-extrabold"
            style={{ backgroundColor: GOLD, color: INK }}
          >
            3
          </span>
          <div>
            <p className="text-xl font-bold tracking-wide uppercase" style={{ color: '#A98600' }}>
              CKD Cooperative
            </p>
            <p className="mt-0.5 text-[15px]" style={{ color: MUTED }}>
              Treatment · Savings · Microloans · Resilience
            </p>
          </div>
        </li>
      </ol>
    </div>
  );
}

const WARRIOR_ACCENT: Record<WarriorAccent, string> = {
  navy: MISSION,
  teal: TEAL,
  gold: GOLD,
  default: MISSION,
};

export function HomePage({ branding }: HomePageProps): JSX.Element {
  const brandName = branding?.brandName ?? 'GSGO';
  const [storyOpen, setStoryOpen] = useState(false);
  const impactSectionRef = useRef<HTMLElement | null>(null);
  const trustSectionRef = useRef<HTMLElement | null>(null);
  const coopSectionRef = useRef<HTMLElement | null>(null);
  const scrollToCrisis = useCallback((): void => {
    document.getElementById('section-crisis')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const map: Array<{ ref: RefObject<HTMLElement | null>; event: string }> = [
      { ref: impactSectionRef, event: 'impact_counter_view' },
      { ref: trustSectionRef, event: 'trust_section_view' },
      { ref: coopSectionRef, event: 'coop_section_view' },
    ];
    const cleanups: Array<() => void> = [];
    for (const { ref, event } of map) {
      const el = ref.current;
      if (!el) continue;
      const o = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            pushAnalyticsEvent({ event });
            o.disconnect();
          }
        },
        { threshold: 0.2 }
      );
      o.observe(el);
      cleanups.push(() => o.disconnect());
    }
    return () => cleanups.forEach((c) => c());
  }, []);

  const c1 = useCountUp(2347);
  const c2 = useCountUp(347);
  const c3 = useCountUp(18);
  const c4 = useCountUp(40);
  const c5 = useCountUp(14);

  return (
    <main id="main-content" className="font-sans text-[#1C2833] bg-white" role="main" aria-label="Home">
      {/* HP-1 — Hero */}
      <section
        className="relative min-h-[100dvh] flex flex-col text-white overflow-hidden"
        style={{ minHeight: '100vh' }}
        aria-label="Hero"
      >
        {HERO_VIDEO ? (
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            poster={heroImage}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        ) : (
          <div
            className="absolute inset-0 w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
            aria-hidden="true"
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(26,82,118,0.75) 0%, rgba(26,82,118,0.55) 60%, rgba(0,0,0,0.8) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 w-full">
          <p
            className="font-extrabold text-lg sm:text-xl tracking-[0.4em] uppercase"
            style={{ color: GOLD }}
          >
            EVERY 3 DAYS
          </p>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-[3.5rem] font-extrabold leading-[1.1] max-w-[760px]">
            A Filipino dies waiting for dialysis they cannot afford.
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-white/90 max-w-[620px] leading-relaxed font-normal">
            {brandName} connects global donors to verified CKD patients in the Philippines — with radical
            transparency and zero bureaucracy.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
            <Link
              to="/donate#donationwidget"
              onClick={() =>
                pushAnalyticsEvent({
                  event: 'cta_click_donate',
                  cta_id: 'hero_give_monthly',
                  cta_label: "GIVE MONTHLY — SAVE A LIFE",
                })
              }
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg font-bold text-base text-[#1C2833] transition-transform hover:scale-[1.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              style={{ backgroundColor: GOLD, boxShadow: '0 8px 24px rgba(212,172,13,0.35)' }}
            >
              GIVE MONTHLY — SAVE A LIFE
            </Link>
            {KYLE_YT ? (
              <button
                type="button"
                onClick={() => {
                  setStoryOpen(true);
                  pushAnalyticsEvent({ event: 'hero_video_open', source: "kyle_story" });
                }}
                className="inline-flex items-center gap-2 text-base text-white underline underline-offset-4 hover:text-[#148F77] transition-colors"
              >
                Watch Kyle&apos;s Story
                <ArrowRight size={16} className="inline" aria-hidden="true" />
              </button>
            ) : (
              <Link
                to="/warriors/kyle"
                className="inline-flex items-center gap-2 text-base text-white underline underline-offset-4 hover:text-[#148F77] transition-colors"
              >
                Watch Kyle&apos;s Story
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            )}
          </div>
          <p
            className="mt-14 sm:mt-20 text-sm tracking-wide text-white/75 text-center sm:text-left max-w-3xl"
            style={{ letterSpacing: '0.05em' }}
          >
            2,347 patients supported | ₱18M+ disbursed | Active in 12 Philippine provinces | Donors from 14
            countries
          </p>
        </div>
        <div className="relative z-10 flex justify-center pb-8">
          <button
            type="button"
            onClick={scrollToCrisis}
            className="p-2 rounded-full text-white/80 hover:text-white animate-bounce"
            aria-label="Scroll to next section"
          >
            <ChevronDown size={28} />
          </button>
        </div>
      </section>

      {storyOpen && KYLE_YT && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Kyle's story"
        >
          <div className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
            <button
              type="button"
              onClick={() => setStoryOpen(false)}
              className="absolute top-2 right-2 z-10 rounded-full bg-white/20 px-3 py-1 text-sm text-white hover:bg-white/30"
            >
              Close
            </button>
            <iframe
              title="Kyle's story — video"
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${KYLE_YT}?autoplay=1&rel=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* HP-2 — Crisis */}
      <section id="section-crisis" className="bg-white py-16 sm:py-24 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold tracking-[0.4em] uppercase" style={{ color: TEAL }}>
            THE REALITY
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold max-w-[700px] mx-auto" style={{ color: MISSION }}>
            CKD is the silent crisis destroying Filipino families.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <Link
              to="/the-crisis"
              onClick={() => pushAnalyticsEvent({ event: 'cta_click_crisis', location: 'hp-2_card_1' })}
              className="block rounded-xl bg-white p-6 border-t-4 shadow-sm hover:shadow-md transition-shadow"
              style={{ borderTopColor: MISSION }}
            >
              <p className="text-5xl sm:text-6xl font-black tabular-nums" style={{ color: MISSION }}>
                1 in 10
              </p>
              <p className="mt-3 text-base text-[#1C2833] leading-relaxed">Filipinos has Chronic Kidney Disease. Most don&apos;t know it.</p>
            </Link>
            <Link
              to="/the-crisis"
              onClick={() => pushAnalyticsEvent({ event: 'cta_click_crisis', location: 'hp-2_card_2' })}
              className="block rounded-xl bg-white p-6 border-t-4 shadow-sm hover:shadow-md transition-shadow"
              style={{ borderTopColor: TEAL }}
            >
              <p className="text-4xl sm:text-5xl font-black tabular-nums" style={{ color: TEAL }}>
                ₱3,500–₱7,000
              </p>
              <p className="mt-3 text-base text-[#1C2833] leading-relaxed">
                cost per dialysis session. 3 sessions needed every week.
              </p>
            </Link>
            <Link
              to="/the-crisis"
              onClick={() => pushAnalyticsEvent({ event: 'cta_click_crisis', location: 'hp-2_card_3' })}
              className="block rounded-xl bg-white p-6 border-t-4 shadow-sm hover:shadow-md transition-shadow"
              style={{ borderTopColor: URGENT }}
            >
              <p className="text-5xl sm:text-6xl font-black tabular-nums" style={{ color: URGENT }}>
                Only 31%
              </p>
              <p className="mt-3 text-base text-[#1C2833] leading-relaxed">
                of CKD patients who need dialysis receive it consistently. PhilHealth covers less than
                half.
              </p>
            </Link>
          </div>
          <p className="mt-10 text-lg sm:text-xl italic max-w-[640px] mx-auto" style={{ color: MISSION }}>
            The gap between what patients need and what they can afford is where people die. GSGO fills that
            gap.
          </p>
          <Link
            to="/the-crisis"
            onClick={() => pushAnalyticsEvent({ event: 'cta_click_crisis', location: 'hp-2_footer' })}
            className="mt-8 inline-flex items-center gap-2 font-medium text-base hover:underline"
            style={{ color: TEAL }}
          >
            See the full crisis report
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* HP-3 — Impact */}
      <section
        ref={impactSectionRef}
        className="py-16 sm:py-20"
        style={{ backgroundColor: MISSION }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-white">
          <p className="text-xs font-bold tracking-[0.4em] uppercase" style={{ color: GOLD }}>
            OUR IMPACT — UPDATED MONTHLY
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
            <div ref={c1.ref} className="p-2">
              <p className="text-4xl sm:text-5xl font-black tabular-nums">{c1.n.toLocaleString()}</p>
              <p className="mt-2 text-sm sm:text-base text-white/75">Dialysis Sessions Funded</p>
            </div>
            <div ref={c2.ref} className="p-2">
              <p className="text-4xl sm:text-5xl font-black tabular-nums">{c2.n.toLocaleString()}</p>
              <p className="mt-2 text-sm sm:text-base text-white/75">Patients Receiving Active Support</p>
            </div>
            <div ref={c3.ref} className="p-2">
              <p className="text-4xl sm:text-5xl font-black tabular-nums" style={{ color: GOLD }}>
                ₱{c3.n}M+
              </p>
              <p className="mt-2 text-sm sm:text-base text-white/75">Disbursed to Patients</p>
            </div>
            <div ref={c4.ref} className="p-2">
              <p className="text-4xl sm:text-5xl font-black tabular-nums">{c4.n.toLocaleString()}</p>
              <p className="mt-2 text-sm sm:text-base text-white/75">CKD Warriors in Network</p>
            </div>
            <div ref={c5.ref} className="p-2">
              <p className="text-4xl sm:text-5xl font-black tabular-nums">{c5.n.toLocaleString()}</p>
              <p className="mt-2 text-sm sm:text-base text-white/75">Countries with GSGO Donors</p>
            </div>
          </div>
          <p className="mt-10 text-base italic text-white/80 max-w-2xl mx-auto">
            Every number above is a real person. Every peso is tracked. Every session matters.
          </p>
        </div>
      </section>

      {/* HP-4 — Warriors */}
      <section className="py-16 sm:py-24" style={{ backgroundColor: BG }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold tracking-[0.4em] uppercase text-center" style={{ color: TEAL }}>
            MEET THE WARRIORS
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-center max-w-[600px] mx-auto" style={{ color: MISSION }}>
            They survived CKD. Now they fight for others.
          </h2>
          <div className="mt-10 flex overflow-x-auto gap-5 pb-4 snap-x snap-mandatory lg:grid lg:grid-cols-4 lg:overflow-visible">
            {WARRIORS.map((w) => {
              const border = WARRIOR_ACCENT[w.accent];
              const topBg = WARRIOR_TOP_BG[w.accent];
              return (
                <Link
                  key={w.to}
                  to={w.to}
                  onClick={() => pushAnalyticsEvent({ event: 'warrior_section_click', warrior: w.name })}
                  className="min-w-[260px] lg:min-w-0 snap-center flex flex-col rounded-2xl overflow-hidden bg-white border shadow-[0_12px_24px_rgba(28,40,51,0.08)] hover:shadow-[0_16px_34px_rgba(28,40,51,0.12)] transition-shadow"
                  style={{ borderColor: `${border}2E` }}
                >
                  <div
                    className="relative aspect-[3/4] w-full overflow-hidden"
                    style={{
                      background: topBg,
                    }}
                  >
                    <span
                      className="absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-1/2 text-8xl font-extrabold uppercase leading-none pointer-events-none"
                      style={{ color: `${MISSION}2A` }}
                      aria-hidden="true"
                    >
                      {w.name.charAt(0)}
                    </span>
                    <span
                      className="absolute bottom-5 left-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm"
                      aria-hidden="true"
                    >
                      <Play size={16} fill="currentColor" />
                    </span>
                  </div>
                  <div className="p-5 sm:p-6">
                    <p className="font-bold text-[1.72rem] leading-[1.22] text-[#20485F]">{w.name}</p>
                    <p
                      className="mt-2 text-[0.82rem] font-semibold uppercase tracking-[0.18em]"
                      style={{ color: border === GOLD ? '#9A7B0A' : border }}
                    >
                      {w.role}
                    </p>
                    <p
                      className="mt-3 text-[1.02rem] leading-relaxed italic"
                      style={{ color: '#304A5A', fontFamily: 'Georgia, "Times New Roman", serif' }}
                    >
                      &ldquo;{w.hook}&rdquo;
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-base font-semibold" style={{ color: TEAL }}>
                      {w.cta}
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/warriors#apply"
              onClick={() => pushAnalyticsEvent({ event: 'warrior_ambassador_cta' })}
              className="inline-flex w-full max-w-[560px] items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 text-center text-sm sm:text-base font-bold uppercase tracking-[0.05em] transition-colors hover:bg-[#148F77] hover:text-white"
              style={{ borderColor: TEAL, color: TEAL }}
            >
              Join the movement — Become a CKD Warrior Ambassador
            </Link>
          </div>
        </div>
      </section>

      {/* HP-5 — How it works */}
      <section ref={trustSectionRef} className="bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold tracking-[0.4em] uppercase" style={{ color: MISSION }}>
            HOW IT WORKS
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold max-w-xl">Your money. Tracked every step.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white"
                style={{ backgroundColor: MISSION }}
              >
                <Lock size={22} aria-hidden="true" />
              </div>
              <p className="text-2xl font-black mt-3 text-slate-300">01</p>
              <p className="mt-2 font-bold text-lg" style={{ color: MISSION }}>
                You give
              </p>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                Donate securely via Stripe (USD) or GCash (PHP). Processed in seconds.
              </p>
            </div>
            <div>
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white"
                style={{ backgroundColor: TEAL }}
              >
                <ShieldCheck size={22} aria-hidden="true" />
              </div>
              <p className="text-2xl font-black mt-3 text-slate-300">02</p>
              <p className="mt-2 font-bold text-lg" style={{ color: TEAL }}>
                GSGO Philippines verifies
              </p>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                Our Philippines team identifies and verifies CKD patients. Medical documents checked.
                Disbursement authorized.
              </p>
            </div>
            <div>
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white"
                style={{ backgroundColor: GOLD, color: INK }}
              >
                <Heart size={22} aria-hidden="true" />
              </div>
              <p className="text-2xl font-black mt-3 text-slate-300">03</p>
              <p className="mt-2 font-bold text-lg" style={{ color: MISSION }}>
                Patients receive treatment
              </p>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                Funds go directly to dialysis centers, pharmacies, or hospitals. Patient receives care. You
                receive a report.
              </p>
            </div>
          </div>
          <blockquote
            className="mt-10 max-w-2xl mx-auto pl-4 py-1 text-center md:text-left border-l-4"
            style={{ borderColor: TEAL, color: MISSION }}
          >
            <p className="text-base italic">
              87 out of every 100 pesos goes directly to patients. Full financial reports published
              quarterly.
            </p>
            <Link to="/about-us#transparency" className="mt-3 inline-block text-sm font-medium" style={{ color: TEAL }}>
              View our financials →
            </Link>
          </blockquote>
        </div>
      </section>

      {/* HP-6 — Donation conversion (links to /donate) */}
      <section
        className="py-16 sm:py-20 text-white text-center"
        style={{ background: `linear-gradient(180deg, ${MISSION} 0%, #0D3349 100%)` }}
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Join the Monthly Warriors Circle</h2>
          <p className="mt-4 text-lg text-white/85 leading-relaxed">
            ₱500 a month keeps one patient in dialysis. That&apos;s less than a cup of coffee a day.
          </p>
          <p className="mt-2 text-sm text-white/60">Default monthly path — adjust amounts and currency on the donate page.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-sm text-white/50">
            <span>Pay securely with:</span>
            <span className="text-white/90">Visa · Mastercard · (Stripe) · PayPal · GCash</span>
          </div>
          <p className="mt-3 text-sm text-white/70 italic">🔒 SSL Secured · 501(c)(3) Nonprofit · 100% Transparent · GCash & Stripe Certified</p>
          <MonthlyWarriorSocialProof variant="dark" className="mt-2" />
          <Link
            to="/donate#donationwidget"
            onClick={() => pushAnalyticsEvent({ event: 'cta_donate', cta_id: 'hp-6_give' })}
            className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-lg font-bold text-lg text-[#1C2833] transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
            style={{ backgroundColor: GOLD, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
          >
            GIVE — OPEN DONATE
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* HP-7 — Cooperative */}
      <section ref={coopSectionRef} className="py-16 sm:py-24" style={{ backgroundColor: BG }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold tracking-[0.4em] uppercase" style={{ color: GOLD }}>
            WHAT MAKES GSGO DIFFERENT
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold" style={{ color: MISSION }}>
            We don&apos;t just collect donations. We build a system.
          </h2>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-6 text-[23px] leading-[1.5]" style={{ color: INK }}>
              <p>
                <strong>Traditional charity:</strong> money in, services out, donors disconnected.
              </p>
              <p>
                <strong>GSGO:</strong> your donation joins a cooperative financial network. Patients build
                savings, access microloans, and grow financial resilience — not just dependency.
              </p>
              <p className="font-semibold" style={{ color: MISSION }}>
                When the system works, patients thrive even after your donation ends.
              </p>
            </div>
            <CooperativeDiagram />
          </div>
          <blockquote
            className="mt-10 pl-4 border-l-4 max-w-2xl text-[22px] italic text-center md:text-left mx-auto md:mx-0"
            style={{ borderColor: TEAL, color: TEAL, fontFamily: 'Georgia, "Playfair Display", serif' }}
          >
            Your donation doesn&apos;t disappear — it becomes part of a system that keeps giving.
          </blockquote>
          <Link
            to="/programs/cooperative"
            onClick={() => pushAnalyticsEvent({ event: 'coop_learn_more' })}
            className="mt-6 inline-flex items-center gap-1 font-medium"
            style={{ color: TEAL }}
          >
            Learn how the cooperative works
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* HP-8 — Partners */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold tracking-[0.4em] uppercase" style={{ color: MISSION }}>
            TRUSTED PARTNERS & AFFILIATES
          </p>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {['NKTI', 'Dialysis PH', 'KHAN', 'Human Organ Preservation Efforts', 'Corporate partners'].map((name) => (
              <div
                key={name}
                className="h-12 px-4 flex items-center justify-center text-sm font-semibold text-slate-500 grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all cursor-default border border-slate-200 rounded-lg"
              >
                {name}
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm" style={{ color: MUTED }}>
            Good Samaritan Global Outreach is a Texas-registered nonprofit. 501(c)(3) application in
            progress. Philippine affiliate operates under GSGO Philippines.
          </p>
        </div>
      </section>

      {/* HP-9 — Newsletter */}
      <section
        className="py-16 sm:py-24"
        style={{ backgroundColor: MISSION }}
        id="newsletter-form"
        aria-label="Newsletter"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold">Join 5,000 people fighting for Filipino CKD patients.</h2>
          <p className="mt-4 text-base sm:text-lg text-white/80">
            Get monthly patient updates, impact reports, and campaign news. No spam. Unsubscribe anytime.
          </p>
          <div className="mt-8 text-left max-w-xl mx-auto">
            <NewsletterSubscribeForm
              source="homepage"
              variant="inline"
              buttonLabel="JOIN THE FIGHT"
              className="[&_input]:border-2 [&_input]:border-[#1A5276] [&_input]:text-[#1C2833] [&_input]:bg-white"
              submitButtonClassName="flex items-center justify-center gap-2 rounded-lg px-6 py-2.5 text-sm font-bold bg-[#D4AC0D] text-[#1C2833] shadow-sm hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#D4AC0D] focus:ring-offset-2 focus:ring-offset-[#1A5276] disabled:opacity-50 transition-all flex-shrink-0"
            />
          </div>
          <p className="mt-3 text-xs text-white/55">We respect your privacy. Your email is never sold or shared.</p>
        </div>
      </section>
    </main>
  );
}
